/**
 * Geometry for the two risk-of-bias figures, so the SVG component only draws.
 *
 * Every word in a figure arrives in `RobLabels`; this file measures and positions it.
 * Coordinates are in viewBox units and left unrounded here — the component rounds them
 * when it draws.
 */

import {
  estimateTextWidth,
  maxTextWidth,
  truncateText,
} from "@/lib/tool-kit/charts/text";
import {
  overallOf,
  type RobLevel,
  type RobStudy,
  type RobTool,
  type SummaryBar,
  type SummarySegment,
} from "@/lib/tool-kit/research-discovery/rob-plot";

export interface RobLabels {
  /** Empty for no title. */
  title: string;
  studyHeader: string;
  /** "D1" … one per domain. */
  domainHeaders: readonly string[];
  overallHeader: string;
  keyHeading: string;
  /** "D1: Bias arising from …", one per domain. */
  domainKeys: readonly string[];
  legendHeading: string;
  levelLabels: Readonly<Record<RobLevel, string>>;
  /** Full domain names for the summary plot's rows, one per domain. */
  domainNames: readonly string[];
  overallName: string;
  axisTitle: string;
}

export const ROB_PAD = 16;
const FONT = 12;
const SMALL = 11;
const TITLE_FONT = 16;
const CELL = 30;
const ROW = 26;
const RADIUS = 10;
const LEGEND_DOT = 9;
const LEGEND_GAP = 18;
const KEY_LINE = 16;
const MIN_WIDTH = 420;
const LABEL_MAX = 220;
const GROUP_GAP = 10;

export interface TextAt {
  x: number;
  y: number;
  text: string;
}

export interface LegendEntry {
  level: RobLevel;
  /** Centre of the dot. */
  cx: number;
  cy: number;
  labelX: number;
  labelY: number;
  label: string;
}

interface Legend {
  heading: TextAt;
  entries: LegendEntry[];
  bottom: number;
}

function layoutLegend(
  levels: readonly RobLevel[],
  labels: RobLabels,
  top: number,
  width: number
): Legend {
  const heading = { x: ROB_PAD, y: top + FONT, text: labels.legendHeading };
  const entries: LegendEntry[] = [];
  let x = ROB_PAD;
  let rowTop = top + FONT + 10;
  for (const level of levels) {
    const label = labels.levelLabels[level];
    const itemWidth =
      LEGEND_DOT * 2 +
      6 +
      estimateTextWidth(label, { fontSize: SMALL }) +
      LEGEND_GAP;
    if (x > ROB_PAD && x + itemWidth - LEGEND_GAP > width - ROB_PAD) {
      x = ROB_PAD;
      rowTop += ROW - 4;
    }
    const cy = rowTop + LEGEND_DOT + 2;
    entries.push({
      level,
      cx: x + LEGEND_DOT,
      cy,
      labelX: x + LEGEND_DOT * 2 + 6,
      labelY: cy + SMALL * 0.35,
      label,
    });
    x += itemWidth;
  }
  return { heading, entries, bottom: rowTop + LEGEND_DOT * 2 + 6 };
}

function legendWidth(levels: readonly RobLevel[], labels: RobLabels): number {
  let total = 0;
  for (const level of levels) {
    total +=
      LEGEND_DOT * 2 +
      6 +
      estimateTextWidth(labels.levelLabels[level], { fontSize: SMALL }) +
      LEGEND_GAP;
  }
  return total - LEGEND_GAP + ROB_PAD * 2;
}

// ── Traffic-light plot ──────────────────────────────────────────────

export interface TrafficCell {
  level: RobLevel;
  cx: number;
  cy: number;
}

export interface TrafficRow {
  name: TextAt;
  cells: TrafficCell[];
}

export interface TrafficLayout {
  width: number;
  height: number;
  radius: number;
  title: TextAt | null;
  /** Column headers, centred on their column. */
  headers: TextAt[];
  studyHeader: TextAt;
  /** x of the line between the domain columns and the overall column, or null. */
  separatorX: number | null;
  gridTop: number;
  gridBottom: number;
  rows: TrafficRow[];
  keyHeading: TextAt;
  keys: TextAt[];
  legend: Legend;
}

export interface TrafficOptions {
  overall: boolean;
}

/** The traffic-light plot: a row per study, a coloured circle per domain. */
export function layoutTrafficLight(
  tool: RobTool,
  studies: readonly RobStudy[],
  labels: RobLabels,
  levels: readonly RobLevel[],
  { overall }: TrafficOptions
): TrafficLayout {
  const names = studies.map((study) =>
    truncateText(study.name, LABEL_MAX, { fontSize: FONT })
  );
  const nameWidth = Math.max(
    maxTextWidth([...names, labels.studyHeader], {
      fontSize: FONT,
      bold: true,
    }),
    40
  );
  const overallWidth = overall
    ? Math.max(
        CELL,
        estimateTextWidth(labels.overallHeader, {
          fontSize: FONT,
          bold: true,
        }) + 8
      )
    : 0;
  const gridLeft = ROB_PAD + nameWidth + 14;
  const domainsRight = gridLeft + tool.domainCount * CELL;
  const overallLeft = domainsRight + GROUP_GAP;
  const gridRight = overall ? overallLeft + overallWidth : domainsRight;

  const titleBlock = labels.title.trim() !== "";
  const headerY = ROB_PAD + (titleBlock ? TITLE_FONT + 14 : 0) + FONT;
  const gridTop = headerY + 10;
  const rows: TrafficRow[] = studies.map((study, index) => {
    const cy = gridTop + index * ROW + ROW / 2;
    const row = [
      ...study.domains.map((level, column) => ({
        level,
        cx: gridLeft + column * CELL + CELL / 2,
        cy,
      })),
    ];
    if (overall) {
      row.push({
        level: overallOf(study),
        cx: overallLeft + overallWidth / 2,
        cy,
      });
    }
    return {
      name: { x: ROB_PAD, y: cy + FONT * 0.35, text: names[index] },
      cells: row,
    };
  });
  const gridBottom = gridTop + studies.length * ROW;

  const headers: TextAt[] = labels.domainHeaders
    .slice(0, tool.domainCount)
    .map((text, column) => ({
      x: gridLeft + column * CELL + CELL / 2,
      y: headerY,
      text,
    }));
  if (overall) {
    headers.push({
      x: overallLeft + overallWidth / 2,
      y: headerY,
      text: labels.overallHeader,
    });
  }

  const keyTop = gridBottom + 18;
  const keyHeading = { x: ROB_PAD, y: keyTop + FONT, text: labels.keyHeading };
  const keys = labels.domainKeys
    .slice(0, tool.domainCount)
    .map((text, index) => ({
      x: ROB_PAD,
      y: keyTop + FONT + 4 + (index + 1) * KEY_LINE,
      text,
    }));
  const keysBottom = keyTop + FONT + 4 + tool.domainCount * KEY_LINE + 6;

  const width = Math.max(
    MIN_WIDTH,
    gridRight + ROB_PAD,
    maxTextWidth(labels.domainKeys, { fontSize: SMALL }) + ROB_PAD * 2,
    legendWidth(levels, labels),
    estimateTextWidth(labels.title, { fontSize: TITLE_FONT, bold: true }) +
      ROB_PAD * 2
  );
  const legend = layoutLegend(levels, labels, keysBottom + 8, width);
  return {
    width,
    height: legend.bottom + ROB_PAD,
    radius: RADIUS,
    title: titleBlock
      ? { x: width / 2, y: ROB_PAD + TITLE_FONT, text: labels.title }
      : null,
    headers,
    studyHeader: { x: ROB_PAD, y: headerY, text: labels.studyHeader },
    separatorX: overall ? domainsRight + GROUP_GAP / 2 : null,
    gridTop,
    gridBottom,
    rows,
    keyHeading,
    keys,
    legend,
  };
}

// ── Summary bar plot ────────────────────────────────────────────────

export interface BarSegment extends SummarySegment {
  x: number;
  width: number;
}

export interface SummaryRow {
  name: TextAt;
  y: number;
  segments: BarSegment[];
}

export interface Tick {
  x: number;
  label: string;
}

export interface SummaryLayout {
  width: number;
  height: number;
  barHeight: number;
  title: TextAt | null;
  barLeft: number;
  barRight: number;
  gridTop: number;
  gridBottom: number;
  ticks: Tick[];
  rows: SummaryRow[];
  axisTitle: TextAt;
  legend: Legend;
}

const BAR_HEIGHT = 26;
const BAR_GAP = 10;
const OVERALL_GAP = 14;
const NAME_MAX = 300;
const BAR_MIN = 340;
const TICKS = [0, 0.25, 0.5, 0.75, 1] as const;

/** The summary plot: a stacked bar per domain, with the overall judgement set apart. */
export function layoutSummary(
  bars: readonly SummaryBar[],
  labels: RobLabels,
  levels: readonly RobLevel[],
  formatPercent: (share: number) => string
): SummaryLayout {
  const names = bars.map((bar) =>
    truncateText(
      bar.domain === "overall"
        ? labels.overallName
        : (labels.domainNames[bar.domain] ?? ""),
      NAME_MAX,
      { fontSize: FONT }
    )
  );
  const nameWidth = maxTextWidth(names, { fontSize: FONT });
  const barLeft = ROB_PAD + nameWidth + 14;
  const titleBlock = labels.title.trim() !== "";
  const barWidth = Math.max(BAR_MIN, MIN_WIDTH + 200 - barLeft - ROB_PAD);
  const barRight = barLeft + barWidth;
  const gridTop = ROB_PAD + (titleBlock ? TITLE_FONT + 14 : 0) + 4;

  let y = gridTop;
  const rows: SummaryRow[] = bars.map((bar, index) => {
    if (bar.domain === "overall") {
      y += OVERALL_GAP;
    }
    const top = y;
    y += BAR_HEIGHT + BAR_GAP;
    let x = barLeft;
    const segments = bar.segments.map((segment) => {
      const width = segment.share * barWidth;
      const placed = { ...segment, x, width };
      x += width;
      return placed;
    });
    return {
      name: {
        x: barLeft - 12,
        y: top + BAR_HEIGHT / 2 + FONT * 0.35,
        text: names[index],
      },
      y: top,
      segments,
    };
  });
  const gridBottom = y - BAR_GAP + 6;
  const ticks = TICKS.map((share) => ({
    x: barLeft + share * barWidth,
    label: formatPercent(share),
  }));
  const axisTitleY = gridBottom + FONT + 22;
  const width = Math.max(
    MIN_WIDTH,
    barRight + ROB_PAD + 8,
    legendWidth(levels, labels),
    estimateTextWidth(labels.title, { fontSize: TITLE_FONT, bold: true }) +
      ROB_PAD * 2
  );
  const legend = layoutLegend(levels, labels, axisTitleY + 14, width);
  return {
    width,
    height: legend.bottom + ROB_PAD,
    barHeight: BAR_HEIGHT,
    title: titleBlock
      ? { x: width / 2, y: ROB_PAD + TITLE_FONT, text: labels.title }
      : null,
    barLeft,
    barRight,
    gridTop: gridTop - 4,
    gridBottom,
    ticks,
    rows,
    axisTitle: {
      x: (barLeft + barRight) / 2,
      y: axisTitleY,
      text: labels.axisTitle,
    },
    legend,
  };
}

export const ROB_SIZES = {
  font: FONT,
  small: SMALL,
  titleFont: TITLE_FONT,
  legendDot: LEGEND_DOT,
} as const;
